<template>
  <!-- Accounting as the app draws it today (Workspace369 origin/main, Oct 22 2026), at the app's own scale.
       Wide containers get the desktop shell (SidebarV2 with Finance open, the canvas header) around
       lib/view/accounting/accounting_hub_screen.dart: the header row (Help, the primary New button from
       widgets/accounting_new_menu.dart), Needs attention (Pending Review and Overdue rows), the metric strip
       (Cash Position, Accounts Receivable, Accounts Payable, Net Income; AppMetricStrip), then Recent journal
       entries (widgets/accounting_recent_entries.dart: Date, Entry #, Memo, Status, Amount) beside Go to
       (Books, Reports & close, Settings). Entry numbers follow journal_number_allocator.dart (JE-2026-0142)
       and the memos are the API's auto-post memos (functions-accounting postingRules.ts). Below 600px it is
       the phone layout: New only, the 2×2 metrics, Recent first as two-line rows, then Go to. Labels are
       app_en.arb strings; Northstar Studio, its clients and amounts are sample data (Harbor & Co.'s invoice
       1031 is the one paid in the Home hero). States: `open` and `posted` (Harbor & Co. pays, the payment
       posts itself as JE-2026-0142, cash rises and receivables fall by $9,690.00). Rows are keyed by position, so
       the new entry rewrites the rows in place instead of moving them (no layout shift). The server renders `posted`. -->
  <div class="lgw" :class="`lgw--${state}`" aria-hidden="true">
    <svg class="lgw-sprite" width="0" height="0">
      <symbol v-for="(d, name) in icons" :id="`lgw-${name}`" :key="name" viewBox="0 0 24 24"><path :d="d" /></symbol>
    </svg>

    <!-- Desktop and tablet -->
    <div class="lgw-desk">
      <aside class="lgw-side">
        <div class="lgw-side-top"><svg class="ic"><use href="#lgw-view_sidebar" /></svg></div>
        <div class="lgw-ws"><span class="lgw-logo">N</span><span class="lgw-ell">Northstar Studio</span><svg class="ic sm"><use href="#lgw-unfold_more" /></svg></div>
        <nav class="lgw-nav">
          <template v-for="g in nav" :key="g.k">
            <div class="lgw-ni top">
              <svg class="ic"><use :href="`#lgw-${g.i}`" /></svg><span>{{ g.k }}</span>
              <svg v-if="g.kids || g.chev" class="ic sm lgw-chev"><use :href="`#lgw-${g.open ? 'expand_more' : 'chevron_right'}`" /></svg>
            </div>
            <template v-if="g.open">
              <div v-for="c in g.kids" :key="c" class="lgw-ni child" :class="{ sel: c === 'Accounting' }"><span>{{ c }}</span></div>
            </template>
          </template>
        </nav>
        <div class="lgw-side-foot">
          <div v-for="f in foot" :key="f.k" class="lgw-ni"><svg class="ic"><use :href="`#lgw-${f.i}`" /></svg><span>{{ f.k }}</span></div>
        </div>
      </aside>

      <div class="lgw-main">
        <header class="lgw-head">
          <span class="lgw-title"><svg class="ic sm"><use href="#lgw-account_balance" /></svg>Accounting</span>
          <span class="lgw-hsearch"><svg class="ic sm"><use href="#lgw-search" /></svg>Search...<kbd>⌘K</kbd></span>
          <span class="lgw-icbtn lgw-bell"><svg class="ic"><use href="#lgw-notifications" /></svg><b>3</b></span>
          <span class="lgw-icbtn"><svg class="ic"><use href="#lgw-settings" /></svg></span>
          <span class="lgw-icbtn"><svg class="ic"><use href="#lgw-chat_bubble_outline" /></svg></span>
        </header>

        <section class="lgw-sheet">
          <div class="lgw-page">
            <div class="lgw-actions">
              <span class="lgw-ghost"><svg class="ic sm"><use href="#lgw-helpOutline" /></svg>Help</span>
              <span class="lgw-btn"><svg class="ic sm"><use href="#lgw-add" /></svg>New</span>
            </div>

            <div class="lgw-label">Needs attention</div>
            <div class="lgw-att"><em class="lgw-tag amber">Pending Review</em><span>1 journal entry pending review</span><svg class="ic sm"><use href="#lgw-chevron_right" /></svg></div>
            <div class="lgw-att"><em class="lgw-tag red">Overdue</em><span>1 overdue bill totaling $640.00</span><svg class="ic sm"><use href="#lgw-chevron_right" /></svg></div>

            <div class="lgw-metrics">
              <div v-for="m in metrics" :key="m.l" class="lgw-m">
                <small><svg class="ic xs"><use :href="`#lgw-${m.i}`" /></svg>{{ m.l }}</small>
                <b :key="`m-${m.v}`" class="lgw-pop">{{ m.v }}</b>
                <span>{{ m.c }}</span>
              </div>
            </div>

            <div class="lgw-cols">
              <div class="lgw-recent">
                <div class="lgw-sh"><b>Recent journal entries</b><span>8</span><span class="lgw-grow" /><span class="lgw-link">View All</span></div>
                <table class="lgw-table">
                  <colgroup><col class="w-date"><col class="w-no"><col class="w-memo"><col class="w-st"><col class="w-amt"></colgroup>
                  <thead><tr><th>Date</th><th>Entry #</th><th>Memo</th><th>Status</th><th class="r">Amount</th></tr></thead>
                  <tbody>
                    <tr v-for="(e, i) in entries" :key="i" :class="{ 'is-new': e.fresh }">
                      <td class="muted">{{ e.date }}</td>
                      <td>{{ e.no }}</td>
                      <td class="lgw-ell">{{ e.memo }}</td>
                      <td><em class="lgw-tag" :class="e.tone">{{ e.st }}</em></td>
                      <td class="r">{{ e.amt }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="lgw-goto">
                <div class="lgw-sh"><b>Go to</b></div>
                <div v-for="g in goto" :key="g.k" class="lgw-gg">
                  <div class="lgw-label">{{ g.k }}</div>
                  <div v-for="r in g.rows" :key="r.k" class="lgw-gr"><svg class="ic sm"><use :href="`#lgw-${r.i}`" /></svg>{{ r.k }}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Phone -->
    <div class="lgw-phone">
      <div class="acp-top"><b>Accounting</b><span class="lgw-btn"><svg class="ic"><use href="#lgw-add" /></svg>New</span></div>
      <div class="acp-body">
        <div class="lgw-label">Needs attention</div>
        <div class="lgw-att"><em class="lgw-tag amber">Pending Review</em><span class="lgw-ell">1 journal entry pending review</span><svg class="ic"><use href="#lgw-chevron_right" /></svg></div>
        <div class="lgw-att"><em class="lgw-tag red">Overdue</em><span class="lgw-ell">1 overdue bill totaling $640.00</span><svg class="ic"><use href="#lgw-chevron_right" /></svg></div>
        <div class="acp-metrics">
          <div v-for="m in metrics" :key="`p-${m.l}`" class="lgw-m">
            <small><svg class="ic"><use :href="`#lgw-${m.i}`" /></svg>{{ m.l }}</small>
            <b :key="`pm-${m.v}`" class="lgw-pop">{{ m.v }}</b>
          </div>
        </div>
        <div class="lgw-sh"><b>Recent journal entries</b><span>8</span><span class="lgw-grow" /><span class="lgw-link">View All</span></div>
        <div v-for="(e, i) in entries.slice(0, 5)" :key="`p-${i}`" class="acp-row" :class="{ 'is-new': e.fresh }">
          <div class="acp-l1"><b>{{ e.no }}</b><span class="lgw-ell">{{ e.memo }}</span><b class="acp-amt">{{ e.amt }}</b></div>
          <div class="acp-l2"><span>{{ e.date }}</span><em class="lgw-tag" :class="e.tone">{{ e.st }}</em></div>
        </div>
      </div>
      <nav class="acp-nav">
        <span v-for="t in tabs" :key="t.k"><svg class="ic"><use :href="`#lgw-${t.i}`" /></svg>{{ t.k }}</span>
        <span class="acp-navmore"><svg class="ic"><use href="#lgw-add" /></svg></span>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { appIcons } from "~/data/app-icons.js";
import { accountingIcons } from "~/data/app-icons-accounting.js";

const props = defineProps({
  // open | posted
  state: { type: String, default: "posted" },
});

const usedApp = ["account_balance_wallet", "add", "auto_awesome", "calendar_today", "chat_bubble_outline", "chevron_right", "dashboard", "expand_more", "forum", "handyman", "logout", "notifications", "people_alt", "receipt_long", "search", "settings", "support_agent", "unfold_more", "view_sidebar", "work_outline", "workspaces"];
const icons = { ...Object.fromEntries(usedApp.map((k) => [k, appIcons[k]])), ...accountingIcons };

const nav = [
  { k: "Dashboard", i: "dashboard" },
  { k: "AI AutoPilot", i: "auto_awesome", chev: true },
  { k: "Clients", i: "people_alt", kids: ["Clients", "Requests"] },
  { k: "Work", i: "work_outline", kids: ["Projects", "Calendar", "Tasks", "Reminders", "Time Tracking"] },
  { k: "Finance", i: "account_balance_wallet", open: true, kids: ["Proposals", "Contracts", "Invoices", "Retainers", "Expenses", "Accounting", "Product Catalog"] },
  { k: "Communications", i: "forum", kids: ["Inbox", "Emails", "Mobile", "Campaigns", "Workflows", "Funnels"] },
  { k: "Tools", i: "handyman", kids: ["Notes", "Files", "Habits"] },
];
const foot = [
  { k: "Settings", i: "settings" },
  { k: "Support", i: "support_agent" },
  { k: "Log Out", i: "logout" },
];
const tabs = [
  { k: "Home", i: "dashboard" },
  { k: "Projects", i: "workspaces" },
  { k: "AI AutoPilot", i: "auto_awesome" },
  { k: "Invoices", i: "receipt_long" },
  { k: "Calendar", i: "calendar_today" },
];

const posted = computed(() => props.state === "posted");

const metrics = computed(() => [
  { l: "Cash Position", i: "account_balance_wallet", v: posted.value ? "$51,070.00" : "$41,380.00", c: "" },
  { l: "Accounts Receivable", i: "arrow_downward", v: posted.value ? "$14,650.00" : "$24,340.00", c: "Tracks money owed to you by customers." },
  { l: "Accounts Payable", i: "arrow_upward", v: "$3,120.00", c: "Tracks money you owe to vendors." },
  { l: "Net Income", i: "trending_up", v: "$42,170.00", c: "Revenue $48,290.00 · Expenses $6,120.00" },
]);

const older = [
  { date: "Oct 22, 2026", no: "JE-2026-0141", memo: 'Auto-posted: Expense "Menu print proofs" — Harbor Print Co., receipt 4471', st: "Posted", tone: "green", amt: "$184.60" },
  { date: "Oct 21, 2026", no: "JE-2026-0140", memo: "Auto-posted: Invoice 1036 for Orchard Veterinary", st: "Posted", tone: "green", amt: "$700.00" },
  { date: "Oct 21, 2026", no: "JE-2026-0139", memo: "Auto-posted: Bill B-2210 from Harbor Print Co.", st: "Pending Review", tone: "amber", amt: "$412.00" },
  { date: "Oct 20, 2026", no: "JE-2026-0138", memo: "October studio rent", st: "Reviewed", tone: "blue", amt: "$1,800.00" },
  { date: "Oct 20, 2026", no: "JE-2026-0137", memo: "Auto-posted: Invoice 1034 for Marlowe Coffee Co.", st: "Posted", tone: "green", amt: "$4,800.00" },
  { date: "Oct 19, 2026", no: "JE-2026-0136", memo: "Auto-posted: Payment received for Invoice 1029", st: "Posted", tone: "green", amt: "$2,250.00" },
  { date: "Oct 19, 2026", no: "JE-2026-0135", memo: 'Auto-posted: Expense "Font licence"', st: "Posted", tone: "green", amt: "$59.00" },
  { date: "Oct 18, 2026", no: "JE-2026-0134", memo: "Auto-posted: Invoice 1035 for Juniper Bakery", st: "Posted", tone: "green", amt: "$1,250.00" },
];
const entries = computed(() =>
  posted.value
    ? [{ date: "Oct 22, 2026", no: "JE-2026-0142", memo: "Auto-posted: Payment received for Invoice 1031", st: "Posted", tone: "green", amt: "$9,690.00", fresh: true }, ...older.slice(0, 7)]
    : older,
);

const goto = [
  { k: "Books", rows: [
    { k: "Journal Entries", i: "menuBook" }, { k: "Chart of Accounts", i: "account_tree" }, { k: "Bills", i: "receipt" },
    { k: "Vendors", i: "storefront" }, { k: "Fixed Assets", i: "precisionManufacturing" }, { k: "Cost Centers", i: "pieChart" },
  ] },
  { k: "Reports & close", rows: [{ k: "Reports", i: "bar_chart" }, { k: "Year-End Close", i: "lockClock" }] },
  { k: "Settings", rows: [{ k: "Accounting Settings", i: "settings" }] },
];
</script>

<style>
/* Design tokens: shadcn Neutral (the app's default scheme) as in InvoicesAppWindow; tag tones from app_tone.dart. */
.lgw {
  --ink: #0a0a0a; --primary: #171717; --on-primary: #fafafa;
  --fill: #eef1f4; --canvas: #f7f7f7; --page: #fff; --edge: #ebebeb; --card-edge: #e6e6e6; --line2: #efefef;
  --ink2: rgb(10 10 10 / 0.62); --ink3: rgb(10 10 10 / 0.5); --quiet: rgb(10 10 10 / 0.32); --selected: rgb(10 10 10 / 0.06); --hair: rgb(10 10 10 / 0.08);
  --gray-t: #5e5e5e; --gray-f: rgb(0 0 0 / 0.067); --green-t: #2d7a4e; --green-f: #e6f4ea; --blue-t: #3f58bf; --blue-f: #ebf0fd;
  --red-t: #ba3a37; --red-f: #fbeaea; --amber-t: #946000; --amber-f: #fdf4c3;
  position: relative;
  container-type: inline-size;
  color: var(--ink);
  font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
  text-align: left;
}
.dark .lgw {
  --ink: #fafafa; --primary: #fafafa; --on-primary: #171717;
  --fill: #232527; --canvas: #121212; --page: #1a1a1a; --edge: #272727; --card-edge: #2c2c2c; --line2: #252525;
  --ink2: rgb(250 250 250 / 0.62); --ink3: rgb(250 250 250 / 0.5); --quiet: rgb(250 250 250 / 0.32); --selected: rgb(250 250 250 / 0.1); --hair: rgb(250 250 250 / 0.1);
  --gray-t: #bdbdbd; --gray-f: rgb(255 255 255 / 0.12); --green-t: #6ed39a; --green-f: rgb(63 178 110 / 0.18); --blue-t: #93aef9; --blue-f: rgb(91 127 234 / 0.2);
  --red-t: #f69591; --red-f: rgb(229 83 78 / 0.2); --amber-t: #f1c95e; --amber-f: rgb(224 170 36 / 0.17);
}
/* The window's size is reserved before its markup has streamed in (see InvoicesAppWindow). */
.lgw { min-height: 640px; }
@media (max-width: 1128px) { .lgw { min-height: 560px; } }
@media (max-width: 648px) { .lgw { min-height: 0; aspect-ratio: 390 / 700; } }
.lgw-sprite { position: absolute; width: 0; height: 0; overflow: hidden; }
.lgw .ic { display: inline-block; flex: none; width: 18px; height: 18px; fill: currentColor; vertical-align: middle; }
.lgw .ic.sm { width: 16px; height: 16px; }
.lgw .ic.xs { width: 14px; height: 14px; }
.lgw-grow { flex: 1; }
.lgw-ell { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lgw .muted { color: var(--ink3); }

/* ---------- desktop shell (as InvoicesAppWindow) ---------- */
.lgw-desk {
  display: grid;
  grid-template-columns: 252px minmax(0, 1fr);
  height: 640px;
  overflow: hidden;
  border: 1px solid var(--card-edge);
  border-radius: 10px;
  background: var(--canvas);
  box-shadow: 0 30px 60px -30px rgb(15 23 42 / 0.28), 0 12px 24px -16px rgb(15 23 42 / 0.12);
  -webkit-mask-image: linear-gradient(#000 90%, transparent);
  mask-image: linear-gradient(#000 90%, transparent);
}
.dark .lgw-desk { border-color: #3a3a3a; box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.06), 0 30px 60px -30px rgb(0 0 0 / 0.7); }
.dark .lgw-phone { border-color: #3a3a3a; }
.lgw-side { display: flex; flex-direction: column; min-height: 0; padding: 0 10px 10px; }
.lgw-side-top { display: flex; align-items: center; height: 48px; padding-left: 6px; color: var(--ink2); }
.lgw-ws { display: flex; align-items: center; gap: 10px; height: 40px; margin-bottom: 6px; border-bottom: 1px solid var(--edge); padding: 0 6px 6px; font-weight: 600; }
.lgw-ws .ic { margin-left: auto; color: var(--ink3); }
.lgw-logo { display: grid; flex: none; place-items: center; width: 26px; height: 26px; border-radius: 6px; background: #171717; color: #fafafa; font-size: 12px; font-weight: 700; }
.dark .lgw-logo { background: #fafafa; color: #171717; }
.lgw-nav { flex: 1; min-height: 0; overflow: hidden; }
.lgw-ni { display: flex; align-items: center; gap: 10px; height: 32px; padding: 0 8px; border-radius: 6px; color: var(--ink2); white-space: nowrap; }
.lgw-ni.top { color: var(--ink); font-weight: 600; }
.lgw-ni.child { padding-left: 38px; }
.lgw-ni.sel { background: var(--selected); color: var(--ink); font-weight: 600; }
.lgw-ni .lgw-chev { margin-left: auto; color: var(--ink3); }
.lgw-side-foot { border-top: 1px solid var(--edge); padding-top: 6px; }
.lgw-side-foot .lgw-ni { height: 30px; }
.lgw-main { display: grid; grid-template-rows: 48px minmax(0, 1fr); min-width: 0; }
.lgw-head { display: flex; align-items: center; gap: 12px; padding: 0 14px 0 4px; }
.lgw-title { display: flex; flex: 1; align-items: center; gap: 8px; font-weight: 600; }
.lgw-title .ic { color: var(--ink2); }
.lgw-hsearch { display: flex; align-items: center; gap: 8px; width: 230px; height: 30px; border: 1px solid var(--card-edge); border-radius: 6px; background: var(--page); padding: 0 6px 0 10px; color: var(--ink3); }
.lgw-hsearch kbd { margin-left: auto; border: 1px solid var(--card-edge); border-radius: 4px; padding: 0 5px; font: inherit; font-size: 11px; }
.lgw-icbtn { position: relative; display: grid; place-items: center; width: 28px; height: 28px; color: var(--ink2); }
.lgw-bell b { position: absolute; top: 0; right: 0; display: grid; place-items: center; min-width: 14px; height: 14px; border-radius: 4px; background: #e53935; color: #fff; font-size: 9px; }
.lgw-sheet { min-height: 0; overflow: hidden; border-top: 1px solid var(--edge); border-left: 1px solid var(--edge); border-top-left-radius: 10px; background: var(--page); }

/* ---------- Accounting hub (accounting_hub_screen.dart, desktop) ---------- */
.lgw-page { max-width: 1200px; margin: 0 auto; padding: 20px 32px 0; }
.lgw-actions { display: flex; justify-content: flex-end; gap: 4px; }
.lgw-ghost { display: inline-flex; align-items: center; gap: 6px; height: 32px; border-radius: 6px; padding: 0 10px; color: var(--ink2); font-weight: 500; }
.lgw-btn { display: inline-flex; flex: none; align-items: center; gap: 6px; height: 32px; border-radius: 6px; background: var(--primary); padding: 0 12px; color: var(--on-primary); font-weight: 500; white-space: nowrap; }
.lgw-label { margin: 16px 0 4px; color: var(--ink3); font-size: 12px; font-weight: 500; }
.lgw-att { display: flex; align-items: center; gap: 10px; height: 32px; margin: 0 -8px; border-radius: 6px; padding: 0 8px; }
.lgw-att span { flex: 1; min-width: 0; }
.lgw-att > .ic { color: var(--quiet); }
.lgw-tag { display: inline-flex; flex: none; align-items: center; height: 20px; border-radius: 4px; background: var(--gray-f); padding: 0 7px; color: var(--gray-t); font-size: 12px; font-style: normal; font-weight: 500; white-space: nowrap; }
.lgw-tag.green { background: var(--green-f); color: var(--green-t); }
.lgw-tag.blue { background: var(--blue-f); color: var(--blue-t); }
.lgw-tag.red { background: var(--red-f); color: var(--red-t); }
.lgw-tag.amber { background: var(--amber-f); color: var(--amber-t); }
.lgw-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-top: 24px; border-top: 1px solid var(--hair); border-bottom: 1px solid var(--hair); }
.lgw-m { display: grid; align-content: start; gap: 4px; min-width: 0; border-left: 1px solid var(--hair); padding: 12px 16px 14px; }
.lgw-m:first-child { border-left: 0; padding-left: 8px; }
.lgw-m small { display: flex; align-items: center; gap: 6px; color: var(--ink2); font-size: 12px; font-weight: 500; white-space: nowrap; }
.lgw-m small .ic { opacity: 0.6; }
.lgw-m b { font-size: 24px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.2; transform-origin: left center; }
.lgw-m span { overflow: hidden; color: var(--ink3); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.lgw-cols { display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); gap: 32px; margin-top: 28px; }
.lgw-sh { display: flex; align-items: center; gap: 8px; height: 32px; }
.lgw-sh b { font-weight: 600; opacity: 0.9; }
.lgw-sh > span:not(.lgw-grow):not(.lgw-link) { color: var(--ink3); }
.lgw-link { color: var(--ink2); font-weight: 500; }
.lgw-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.lgw-table .w-date { width: 112px; } .lgw-table .w-no { width: 84px; } .lgw-table .w-st { width: 132px; } .lgw-table .w-amt { width: 104px; }
.lgw-table th { height: 32px; border-bottom: 1px solid var(--line2); padding: 0 8px 0 0; color: rgb(from var(--ink) r g b / 0.52); font-weight: 500; text-align: left; white-space: nowrap; }
.lgw-table td { height: 36px; border-bottom: 1px solid var(--line2); overflow: hidden; padding: 0 8px 0 0; text-overflow: ellipsis; white-space: nowrap; }
.lgw-table td:nth-child(2) { font-weight: 500; }
.lgw-table .r { padding-right: 0; text-align: right; }
.lgw-table tr.is-new td { animation: lgw-wash 2.4s ease-out both; }
.lgw-gg .lgw-label { margin: 8px 0 2px; }
.lgw-gr { display: flex; align-items: center; gap: 10px; height: 30px; color: var(--ink); }
.lgw-gr .ic { color: var(--ink2); }
.lgw-pop { animation: lgw-pop 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
@keyframes lgw-pop { from { transform: scale(0.92); opacity: 0.35; } to { transform: none; opacity: 1; } }
@keyframes lgw-wash { from { background: var(--selected); } to { background: transparent; } }

/* Tablet: the sidebar is collapsed, as the app's sidebar toggle leaves it; below the app's 960px two-column
   breakpoint Go to stacks under Recent, so the window shows Recent alone. */
@container (max-width: 1080px) {
  .lgw-desk { grid-template-columns: minmax(0, 1fr); height: 560px; }
  .lgw-side, .lgw-hsearch { display: none; }
  .lgw-head { padding-left: 14px; }
  .lgw-page { padding: 16px 24px 0; }
  .lgw-cols { grid-template-columns: minmax(0, 1fr); }
  .lgw-goto { display: none; }
}
@container (max-width: 860px) {
  .lgw-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .lgw-m:nth-child(3) { border-left: 0; padding-left: 8px; }
  .lgw-m:nth-child(n+3) { border-top: 1px solid var(--hair); }
}

/* ---------- phone (one app point = 1/390 of the width, text at the app's default 125%) ---------- */
.lgw-phone { display: none; }
@container (max-width: 600px) {
  .lgw-desk { display: none; }
  .lgw-phone {
    --u: calc(100cqi / 390);
    --t: calc(var(--u) * 1.25);
    position: relative;
    display: block;
    height: calc(var(--u) * 700);
    overflow: hidden;
    border: 1px solid var(--card-edge);
    border-radius: 10px;
    background: var(--page);
    font-size: calc(var(--t) * 13);
  }
  .lgw-phone .ic { width: calc(var(--t) * 15); height: calc(var(--t) * 15); }
  .acp-top { display: flex; align-items: center; justify-content: space-between; height: calc(var(--u) * 52); padding: 0 calc(var(--u) * 12); }
  .acp-top b { font-size: calc(var(--t) * 18); font-weight: 600; }
  .lgw-phone .lgw-btn { height: calc(var(--u) * 40); gap: calc(var(--u) * 4); padding: 0 calc(var(--u) * 14); }
  .acp-body { padding: 0 calc(var(--u) * 12); }
  .lgw-phone .lgw-label { margin: calc(var(--u) * 4) 0 calc(var(--u) * 2); font-size: calc(var(--t) * 11); }
  .lgw-phone .lgw-att { gap: calc(var(--u) * 8); height: calc(var(--u) * 40); margin: 0; padding: 0; }
  .lgw-phone .lgw-tag { height: calc(var(--t) * 19); padding: 0 calc(var(--u) * 6); font-size: calc(var(--t) * 11); }
  .acp-metrics { display: grid; grid-template-columns: 1fr 1fr; margin: calc(var(--u) * 12) 0 calc(var(--u) * 8); border-top: 1px solid var(--hair); border-bottom: 1px solid var(--hair); }
  .acp-metrics .lgw-m { gap: calc(var(--u) * 3); padding: calc(var(--u) * 10) calc(var(--u) * 10); }
  .acp-metrics .lgw-m:nth-child(odd) { border-left: 0; padding-left: 0; }
  .acp-metrics .lgw-m:nth-child(n+3) { border-top: 1px solid var(--hair); }
  .acp-metrics small { gap: calc(var(--u) * 4); font-size: calc(var(--t) * 11); }
  .acp-metrics small .ic { width: calc(var(--t) * 12); height: calc(var(--t) * 12); }
  .acp-metrics b { font-size: calc(var(--t) * 17); }
  .lgw-phone .lgw-sh { height: calc(var(--u) * 40); }
  .acp-row { display: grid; gap: calc(var(--u) * 4); border-top: 1px solid var(--line2); padding: calc(var(--u) * 8) 0; }
  .acp-row.is-new { animation: lgw-wash 2.4s ease-out both; }
  .acp-l1 { display: flex; align-items: baseline; gap: calc(var(--u) * 8); min-width: 0; }
  .acp-l1 b { flex: none; font-weight: 500; }
  .acp-l1 span { flex: 1; min-width: 0; color: rgb(from var(--ink) r g b / 0.6); }
  .acp-l1 .acp-amt { font-weight: 600; }
  .acp-l2 { display: flex; align-items: center; justify-content: space-between; color: var(--ink3); font-size: calc(var(--t) * 12); }
  .acp-nav { position: absolute; z-index: 3; right: calc(var(--u) * 12); bottom: calc(var(--u) * 14); left: calc(var(--u) * 12); display: flex; align-items: center; gap: calc(var(--u) * 4); border: 1px solid var(--card-edge); border-radius: 10px; background: var(--page); padding: calc(var(--u) * 6); box-shadow: 0 8px 24px rgb(0 0 0 / 0.12), 0 -14px 18px -6px var(--page); }
  .acp-nav::after { content: ""; position: absolute; right: calc(var(--u) * -13); bottom: calc(var(--u) * -15); left: calc(var(--u) * -13); height: calc(var(--u) * 14); background: var(--page); }
  .acp-nav > span:not(.acp-navmore) { display: grid; flex: 1; justify-items: center; gap: calc(var(--u) * 2); color: var(--ink3); font-size: calc(var(--u) * 10.5); white-space: nowrap; }
  .acp-nav .ic { width: calc(var(--u) * 22); height: calc(var(--u) * 22); }
  .acp-navmore { display: grid; flex: none; place-items: center; width: calc(var(--u) * 44); height: calc(var(--u) * 44); border-radius: 8px; background: var(--primary); color: var(--on-primary); }
}
@media (prefers-reduced-motion: reduce) { .lgw * { animation: none !important; transition: none !important; } }
</style>
